<template>
  <div class="notice-dialog" :class="{ 'is-visible': isVisible }">
    <div class="notice-backdrop" @click="handleBackdropClick"></div>

    <div class="notice-content">
      <div class="notice-header" v-if="title">
        <h3 class="notice-title">{{ title }}</h3>
        <button
          v-if="canClose && closeOnBackdrop"
          class="notice-close"
          @click="close"
          aria-label="Chiudi"
        >
          <span class="icon-close"></span>
        </button>
      </div>

      <div class="notice-body">
        <div v-if="contentHtml" class="notice-message" v-html="contentHtml"></div>
        <div v-else class="notice-message">{{ content }}</div>

        <slot name="content"></slot>
      </div>

      <div class="notice-footer">
        <slot name="actions">
          <button
            v-if="showCancelButton"
            class="btn-cancel"
            @click="handleCancel"
            :disabled="confirmDisabled"
          >
            {{ cancelButtonText }}
          </button>

          <!-- Nuovo pulsante per azione secondaria -->
          <button
            v-if="showSecondButton"
            class="btn-secondary"
            @click="handleSecondAction"
            :disabled="confirmDisabled"
          >
            {{ secondButtonText }}
          </button>

          <button
            class="btn-confirm"
            @click="handleConfirm"
            :disabled="confirmDisabled"
          >
            {{ confirmButtonText }}
          </button>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NoticeDialog',

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: ''
    },
    contentHtml: {
      type: String,
      default: ''
    },
    showCancelButton: {
      type: Boolean,
      default: false
    },
    cancelButtonText: {
      type: String,
      default: 'Annulla'
    },
    confirmButtonText: {
      type: String,
      default: 'Conferma'
    },
    confirmDisabled: {
      type: Boolean,
      default: false
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    autoFocus: {
      type: Boolean,
      default: true
    },
    canClose: {
      type: Boolean,
      default: true
    },
    // Nuove props per il secondo pulsante
    showSecondButton: {
      type: Boolean,
      default: false
    },
    secondButtonText: {
      type: String,
      default: 'Azione aggiuntiva'
    }
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    }
  },

  mounted() {
    if (this.isVisible) {
      this.handleOpen();
    }

    // Add global event listeners
    document.addEventListener('keydown', this.handleKeyDown);
  },

  beforeDestroy() {
    // Remove global event listeners
    document.removeEventListener('keydown', this.handleKeyDown);

    // Remove body class if the dialog is still open
    if (this.isVisible) {
      document.body.classList.remove('notice-dialog-open');
    }
  },

  methods: {
    handleOpen() {
      // Prevent body scrolling when dialog is open
      document.body.classList.add('notice-dialog-open');

      // Focus the confirm button if autoFocus is enabled
      if (this.autoFocus) {
        this.$nextTick(() => {
          const confirmButton = this.$el.querySelector('.btn-confirm');
          if (confirmButton) {
            confirmButton.focus();
          }
        });
      }
    },

    handleClose() {
      // Re-enable body scrolling
      document.body.classList.remove('notice-dialog-open');
    },

    close() {
      if (!this.canClose) return;
      this.$emit('close');
    },

    handleConfirm() {
      if (this.confirmDisabled) return;
      this.$emit('confirm');
      if (!this.persistent) {
        this.close();
      }
    },

    handleCancel() {
      if (this.confirmDisabled) return;
      this.$emit('cancel');
      this.close();
    },

    // Nuovo metodo per gestire l'azione secondaria
    handleSecondAction() {
      if (this.confirmDisabled) return;
      this.$emit('second-action');
      this.close();
    },

    handleBackdropClick() {
      if (this.closeOnBackdrop && this.canClose) {
        this.close();
      }
    },

    handleKeyDown(e) {
      if (!this.isVisible || !this.canClose) return;

      // Close on escape key
      if (e.keyCode === 27) {
        this.close();
      }

      // Confirm on enter key
      if (e.keyCode === 13 && !this.confirmDisabled) {
        this.handleConfirm();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.notice-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;

  &.is-visible {
    opacity: 1;
    visibility: visible;
  }

  .notice-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: -1;
  }

  .notice-content {
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    transform: translateY(20px);
    transition: transform 0.3s;

    .is-visible & {
      transform: translateY(0);
    }
  }

  .notice-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #e5e5e5;

    .notice-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
    }

    .notice-close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 50%;
      }

      .icon-close {
        display: inline-block;
        width: 16px;
        height: 16px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='18' y1='6' x2='6' y2='18'%3E%3C/line%3E%3Cline x1='6' y1='6' x2='18' y2='18'%3E%3C/line%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
      }
    }
  }

  .notice-body {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;

    .notice-message {
      font-size: 1rem;
      line-height: 1.5;
      color: #333;

      p {
        margin-top: 0;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .notice-footer {
    display: flex;
    justify-content: flex-end;
    padding: 1rem;
    border-top: 1px solid #e5e5e5;

    button {
      min-width: 100px;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.2s;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &.btn-cancel {
        background-color: transparent;
        border: 1px solid #e5e5e5;
        color: #666;
        margin-right: 0.5rem;

        &:hover:not(:disabled) {
          background-color: #f5f5f5;
        }
      }

      /* Stile per il nuovo pulsante secondario */
      &.btn-secondary {
        background-color: #217867;
        border: 1px solid #217867;
        color: white;
        margin-right: 0.5rem;

        &:hover:not(:disabled) {
          background-color: darken(#217867, 5%);
        }
      }

      &.btn-confirm {
        background-color: #0a5741;
        border: 1px solid #0a5741;
        color: white;

        &:hover:not(:disabled) {
          background-color: darken(#0a5741, 5%);
        }
      }
    }
  }
}

// Global style for body when dialog is open
:global(body.notice-dialog-open) {
  // overflow: hidden;
}

// Responsive adjustments
@media (max-width: 576px) {
  .notice-dialog {
    .notice-content {
      max-width: 90%;
      margin: 0 20px;
    }

    .notice-footer {
      button {
        min-width: 80px;
        font-size: 0.875rem;
      }
    }
  }
}
</style>